/**
 * ENG-4897 — pure decision helper for `.mcp.json` drift detection.
 *
 * Extracted from manager-worker.ts so unit tests can exercise the
 * decision tree without importing the entire worker (which has heavy
 * side effects at module load).
 *
 * The manager polls every 10s. After it deploys the latest `.mcp.json`
 * to a persistent claude session's project directory, it asks: has the
 * file changed since the running claude was launched? Three outcomes
 * matter:
 *   - No file on disk yet → nothing to compare.
 *   - First observation (no recorded baseline) → adopt current as baseline.
 *   - Mismatch → schedule a tmux respawn so claude reads the fresh config.
 *
 * The optimistic baseline (we adopt whatever's on disk on first poll
 * after manager start) is the trade-off documented in the issue. Worst
 * case is one missed drift event after a manager restart; the next
 * change is caught.
 */

import { createHash } from 'node:crypto';

export type McpDriftAction =
  | { kind: 'no-config' }
  | { kind: 'baseline'; hash: string }
  | { kind: 'no-drift' }
  | { kind: 'drift'; previous: string; current: string };

export function decideMcpDriftAction(
  currentHash: string | null,
  knownHash: string | undefined,
): McpDriftAction {
  if (!currentHash) return { kind: 'no-config' };
  if (!knownHash) return { kind: 'baseline', hash: currentHash };
  if (knownHash === currentHash) return { kind: 'no-drift' };
  return { kind: 'drift', previous: knownHash, current: currentHash };
}

/**
 * ENG-5537 — given a content `drift` was detected, decide whether it warrants
 * restarting the running claude session.
 *
 * Only a *membership* change to the declared MCP server set (a server added or
 * removed) requires a restart: the running session connected to a fixed set of
 * servers at launch, and gaining/losing one means the live session's tool
 * surface is wrong until it respawns. A *value-only* drift — the same set of
 * servers with a rotated url or header value, e.g. Composio re-signing its
 * `mcp_url` on most polls — leaves the running session's established
 * connections intact and is picked up by the next natural respawn, so it must
 * NOT restart. Restarting on value-only drift is exactly what flapped agents
 * into the ENG-5441 circuit breaker (phil, agt-aws-1, 2026-05-25).
 *
 * When either key set is unknown (file missing / unparseable on one side), we
 * keep the pre-ENG-5537 safe default: treat it as restart-worthy.
 */
export interface McpDriftRestartDecision {
  /** Restart the running session? True only on membership change or unknown. */
  restart: boolean;
  /** Key membership could not be determined (null/undefined on either side). */
  membershipUnknown: boolean;
  /** Server keys added or removed (sorted; empty for value-only or unknown). */
  addedOrRemoved: string[];
}

export function decideMcpRestartOnDrift(
  previousKeys: Set<string> | null | undefined,
  currentKeys: Set<string> | null | undefined,
): McpDriftRestartDecision {
  if (!previousKeys || !currentKeys) {
    return { restart: true, membershipUnknown: true, addedOrRemoved: [] };
  }
  const addedOrRemoved: string[] = [];
  for (const k of currentKeys) if (!previousKeys.has(k)) addedOrRemoved.push(k);
  for (const k of previousKeys) if (!currentKeys.has(k)) addedOrRemoved.push(k);
  addedOrRemoved.sort();
  return { restart: addedOrRemoved.length > 0, membershipUnknown: false, addedOrRemoved };
}

/**
 * ENG-5537 — structure-only hash of a managed-toolkit desired set, used to gate
 * the convergence-driven session restart independently of the value hash that
 * gates the `.mcp.json` write.
 *
 * Deliberately hashes only each server's id and the SORTED NAMES of its header
 * keys — never the url or header values. A provider rotating a signed `mcp_url`
 * (or a credential-bearing header value) moves the value hash, so `.mcp.json`
 * re-converges to the live credential, but leaves THIS hash unchanged, so the
 * running session is left alone. Adding/removing a toolkit, or a header key
 * appearing/disappearing, does move this hash and triggers the restart.
 */
export function managedMcpStructureHash(
  entries: Array<{ serverId: string; headers?: Record<string, string> }>,
): string {
  const basis = entries
    .slice()
    .sort((a, b) => a.serverId.localeCompare(b.serverId))
    .map((e) => `${e.serverId}|${Object.keys(e.headers ?? {}).sort().join(',')}`)
    .join('\n');
  return createHash('sha256').update(basis).digest('hex').slice(0, 16);
}

/**
 * ENG-7576 (PR 2) — derive the SAME structure-only hash as
 * {@link managedMcpStructureHash}, but from a parsed on-disk `.mcp.json`
 * instead of the desired-entries list. Used to snapshot what managed-MCP
 * structure the running session was actually LAUNCHED with, so the
 * convergence restart can be suppressed when the desired structure is already
 * live in the session (the cross-poll de-dup the one-timer-per-agent
 * coalescer cannot provide).
 *
 * Both derivations must stay hash-compatible: `writeMcpServer` writes each
 * desired entry's `headers` verbatim under `mcpServers[serverId]`, so the
 * serverId + sorted-header-KEY basis is identical on both sides. If they ever
 * diverge, the failure mode is fail-open (launch hash never equals desired
 * hash → no suppression → pre-ENG-7576 restart behaviour), never a wrongly
 * suppressed restart.
 *
 * Returns null when the config is missing/unparseable (caller seeds no
 * anchor); a config with no managed servers hashes the empty set, which
 * correctly equals `managedMcpStructureHash([])` for an empty desired set.
 */
export function managedMcpStructureHashFromFile(
  mcpConfig:
    | { mcpServers?: Record<string, { headers?: Record<string, string> } | undefined> }
    | null
    | undefined,
  isManagedKey: (key: string) => boolean,
): string | null {
  if (!mcpConfig || typeof mcpConfig !== 'object') return null;
  const servers = mcpConfig.mcpServers;
  if (!servers || typeof servers !== 'object') return managedMcpStructureHash([]);
  const entries: Array<{ serverId: string; headers?: Record<string, string> }> = [];
  for (const [key, value] of Object.entries(servers)) {
    if (!isManagedKey(key)) continue;
    const headers =
      value && typeof value === 'object' && value.headers && typeof value.headers === 'object'
        ? value.headers
        : undefined;
    entries.push({ serverId: key, headers });
  }
  return managedMcpStructureHash(entries);
}

/**
 * ENG-7576 (PR 2) — pure decision for the managed-toolkit convergence's
 * session restart, extracted (like {@link decideMcpRestartOnDrift}) so the
 * multi-trigger integration-add sequence is unit-testable.
 *
 * The tree preserves the pre-ENG-7576 gates in their original meaning and
 * adds ONE new branch: `suppress-already-live`, taken when the running
 * session's launch-state anchor (the structure hash of the `.mcp.json` it was
 * spawned with) already equals the desired structure — i.e. the change some
 * baseline re-detected is already live in the session, so a restart would be
 * pure churn. An absent anchor (undefined/null: seeding failed, or a session
 * predating the manager restart) NEVER suppresses — fail-open to the
 * pre-ENG-7576 behaviour.
 *
 * Deliberately NOT consulted by bind-remediation or the channel-secret drift
 * trigger: bind-remediation exists precisely for "config matches but the
 * bind failed" (a launch-state comparison cannot see that), and channel
 * secrets rotate value-only (invisible to a structure hash).
 */
export type ManagedMcpRestartDecision =
  | { kind: 'restart' }
  | { kind: 'suppress-already-live' }
  | { kind: 'skip-first-convergence' }
  | { kind: 'skip-no-change' }
  | { kind: 'skip-unhealthy' }
  | { kind: 'skip-flapping' }
  | { kind: 'skip-prune-deferred' };

export function decideManagedMcpRestart(input: {
  /** Last structure hash the convergence recorded (agent-scoped baseline). */
  prevStructureHash: string | undefined;
  /** Structure hash of the CURRENT desired managed-toolkit set. */
  structureHash: string;
  /** Structure hash of the `.mcp.json` the running session launched with. */
  launchStructureHash: string | null | undefined;
  sessionHealthy: boolean;
  /**
   * ENG-6123 flap dampener verdict — wins over the health check and everything
   * below it, but NOT over `deferringPrune`. See the ranking note at the branch:
   * flapping asks whether a restart can safely happen now, whereas a deferred
   * prune means the change being restarted for has not happened at all yet.
   */
  flapping: boolean;
  /**
   * ENG-9708 — a managed-toolkit prune for this agent is still awaiting its N
   * consecutive confirmations, so the desired set has already dropped a server
   * that is deliberately still in `.mcp.json`.
   *
   * The structural change the caller is holding here is one it has NOT applied.
   * Restarting on it would churn the session and inject "managed MCP tool
   * servers were removed" for servers the file still declares — the notice
   * would be false and the session would come back with them still loaded.
   * The caller also withholds the baseline while this is true, so the restart
   * is not lost: it fires on the cycle the prune actually lands.
   *
   * Optional so existing callers (bind-remediation's tests, the drift trigger)
   * are unaffected; absent means "nothing deferred", the pre-ENG-9708 tree.
   */
  deferringPrune?: boolean;
}): ManagedMcpRestartDecision {
  const { prevStructureHash, structureHash, launchStructureHash, sessionHealthy, flapping } = input;
  if (prevStructureHash === undefined) return { kind: 'skip-first-convergence' };
  if (structureHash === prevStructureHash) return { kind: 'skip-no-change' };
  // Ranked ABOVE health and flapping deliberately: those two ask whether a
  // restart can safely happen now, this one says the change being restarted for
  // has not happened at all yet.
  if (input.deferringPrune === true) return { kind: 'skip-prune-deferred' };
  if (!sessionHealthy) return { kind: 'skip-unhealthy' };
  if (flapping) return { kind: 'skip-flapping' };
  if (launchStructureHash != null && launchStructureHash === structureHash) {
    return { kind: 'suppress-already-live' };
  }
  return { kind: 'restart' };
}

/**
 * ENG-6062 — VALUE hash of the channel-owned secrets in `.env.integrations`.
 *
 * The deliberate counterpart to {@link managedMcpStructureHash}: that hash is
 * value-BLIND (ENG-5537) so a Composio `mcp_url` / header re-sign on most polls
 * doesn't flap the session into the ENG-5441 breaker. But after ENG-5901
 * Track D, channel tokens (SLACK_BOT_TOKEN, TELEGRAM_BOT_TOKEN,
 * MSTEAMS_CLIENT_SECRET, …) are `${VAR}` templates in `.mcp.json` with the raw
 * value in `.env.integrations`. A token rotation changes only that value —
 * `.mcp.json` stays byte-identical — so neither the `.mcp.json` content hash nor
 * the structure hash moves, and the running channel MCP keeps the old, now
 * provider-revoked token until something else restarts the session (observed:
 * Vigil's Telegram outage, agt-aws-1, 2026-06-05).
 *
 * This hash IS value-sensitive, but scoped to the fixed
 * `channelSecretKeys` set — those rotate only on a deliberate operator action,
 * never per-poll, so reacting to them cannot reintroduce the ENG-5441 flap
 * (writeChannelCredentials upserts the same value idempotently between
 * rotations → stable hash). Pass `CHANNEL_SECRET_ENV_KEYS` from
 * `@augmented/core` as the key set so the list stays single-sourced with the
 * writers.
 *
 * Returns null when none of the keys are present (no channel secrets to track →
 * no drift signal, handled as `no-config` by {@link decideMcpDriftAction}).
 */
export function channelSecretValueHash(
  envEntries: Record<string, string> | null | undefined,
  channelSecretKeys: readonly string[],
): string | null {
  if (!envEntries) return null;
  const basis = channelSecretKeys
    .slice()
    .sort()
    .filter((k) => Object.prototype.hasOwnProperty.call(envEntries, k))
    .map((k) => `${k}=${envEntries[k]}`)
    .join('\n');
  if (basis.length === 0) return null;
  return createHash('sha256').update(basis).digest('hex').slice(0, 16);
}
